/**
 * SYN-1519 — Política de senha: fonte ÚNICA da regra no frontend.
 *
 * Espelha `app/schemas/auth.py::validate_password_policy` do backend, na mesma
 * ordem: mínimo 8 caracteres → no máximo 72 BYTES → ao menos uma letra → ao
 * menos um número. Símbolo NÃO é exigido (aumenta abandono no primeiro contato
 * sem ganho proporcional).
 *
 * O teto é em **bytes**, não em caracteres, porque é o limite real do bcrypt
 * (ele levanta acima de 72 bytes, não trunca). Letra acentuada custa mais de um
 * byte: `'ção'` são 3 caracteres e 5 bytes, e `'ção'` × 24 são 72 caracteres e
 * 120 bytes — ou seja, uma frase-senha em português estoura o limite antes dos
 * 72 caracteres. Validar por `.length` está errado por construção.
 *
 * Single source of truth for the client-side password policy; mirrors the
 * backend byte-based ceiling instead of a character count.
 */
import { computed, unref } from 'vue'

export const PASSWORD_MIN_LENGTH = 8
export const PASSWORD_MAX_BYTES = 72

/** Comprimento em bytes UTF-8 — o que o bcrypt do backend realmente conta. */
export function passwordByteLength(value) {
    return new TextEncoder().encode(value).length
}

/**
 * @param {import('vue').Ref<string>|string} password
 * @returns {{
 *   requirements: import('vue').ComputedRef<Array<{key: string, met: boolean}>>,
 *   byteLength: import('vue').ComputedRef<number>,
 *   withinMaxBytes: import('vue').ComputedRef<boolean>,
 *   isValid: import('vue').ComputedRef<boolean>,
 *   strength: import('vue').ComputedRef<{level: string, percent: number}|null>,
 * }}
 */
export function usePasswordPolicy(password) {
    const value = computed(() => unref(password) || '')

    const byteLength = computed(() => passwordByteLength(value.value))
    const withinMaxBytes = computed(() => byteLength.value <= PASSWORD_MAX_BYTES)

    // Ordem = ordem do checklist na tela. `\p{L}` / `\p{Nd}` para casar com
    // `str.isalpha()` / `str.isdigit()` do Python, que são unicode-aware.
    const requirements = computed(() => [
        { key: 'minLength', met: value.value.length >= PASSWORD_MIN_LENGTH },
        { key: 'letter', met: /\p{L}/u.test(value.value) },
        { key: 'number', met: /\p{Nd}/u.test(value.value) },
    ])

    const isValid = computed(
        () => withinMaxBytes.value && requirements.value.every((r) => r.met),
    )

    // `null` enquanto o campo está vazio: a barra só aparece depois da primeira
    // tecla, para não receber a pessoa com um medidor vermelho de senha vazia.
    const strength = computed(() => {
        if (!value.value) return null
        if (!withinMaxBytes.value) return { level: 'tooLong', percent: 100 }

        const met = requirements.value.filter((r) => r.met).length
        const score = met + (value.value.length >= 12 ? 1 : 0)
        const level = score <= 1 ? 'weak' : score === 4 ? 'strong' : 'medium'

        return { level, percent: (score / 4) * 100 }
    })

    return { requirements, byteLength, withinMaxBytes, isValid, strength }
}
